import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AppButton, Box, Text } from '@common-fe/common-fe';
import dayjs from 'dayjs';
import styled from 'styled-components';

import { DATE_TOOLTIP_FORMAT } from '@/common/constants';
import useAccountOrdersQuery from '@/modules/transaction/queries/useAccountOrders.query';
import { PaymentType, TransactionData } from '@/modules/transaction/transaction.types';
import { useAuthStore } from '@/modules/user/stores';

import useClaimActivitiesMessages from './message/useClaimActivitiesMessages';

const ShowMoreBox = styled(Box)`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
`;

const MIN_COUNT_ACTIVITIES = 3;
const SHOW_MORE_BOX_BACKGROUND = 'linear-gradient(0deg, #F7F9FA 30.54%, rgba(247, 249, 250, 0) 100%)';

interface Props {
  data: TransactionData;
  onUploadDocument?: () => void;
  onUploadLmnDocument?: () => void;
}

const TransactionActivities: React.FC<Props> = ({ data, onUploadDocument, onUploadLmnDocument }) => {
  const { t } = useTranslation();
  const [expand, setExpand] = useState(false);
  const { user } = useAuthStore();

  const { accountOrders } = useAccountOrdersQuery({
    serviceId: data?.service?.id,
    serviceOn: data?.serviceOn,
    dependentId: data?.claimActorDto?.dependentId,
    claimValue: data?.amount,
  });

  const validAccountOrders = useMemo(() => accountOrders?.filter((accountOrder) => {
    if (!accountOrder?.submitClaimsDateEnd) {
      return true;
    }
    const currentTime =  dayjs().startOf('day');
    const submitClaimsDateEnd = dayjs(accountOrder.submitClaimsDateEnd).startOf('day');
    return submitClaimsDateEnd.isAfter(currentTime) || submitClaimsDateEnd.isSame(currentTime);
  }), [accountOrders]);

  const isCardSwipe = useMemo(() => data?.paymentType === PaymentType.CARD_SWIPE, [data?.paymentType]);

  const isUploadButtonEnabled = useMemo(() => isCardSwipe || validAccountOrders?.length > 0 || !accountOrders?.length,
    [isCardSwipe, validAccountOrders?.length, accountOrders?.length]);

  const { dates, messagesSortedByDate, allActivitiesCount } = useClaimActivitiesMessages({
    data,
    employeeFirstName: user?.firstName,
    onUploadDocument,
    onUploadLmnDocument,
    isUploadButtonEnabled,
  });

  if (!dates.length || !allActivitiesCount || !user?.firstName) return null;

  return (
    <Box
      pad="spacing24"
      background="module"
      round="moduleRound"
      overflow="hidden"
      style={{ position: 'relative' }}
      margin={{ top: 'spacing24' }}
      data-testid="transaction-activities-wrapper"
    >
      <Box height={{ min: 'fit-content' }}>
        <Text size="16px" weight={700} data-testid="transaction-activities-title">
          {t('Activities')}
        </Text>
        {dates.map((date) => {
          const messages = messagesSortedByDate.find((item) => item.date === date)?.messages || [];
          return (
            <Box key={date}>
              <Box margin={{ top: 'spacing24', bottom: 'spacing16' }} direction="row" align="center">
                <Box margin={{ right: 'spacing12' }} data-testid="transaction-activities-date">
                  <Text size="14px" style={{ whiteSpace: 'nowrap' }}>
                    {dayjs(date).format(DATE_TOOLTIP_FORMAT)}
                  </Text>
                </Box>

                <Box height="1px" background="border1" style={{ flex: 1 }} />
              </Box>

              {messages.slice(0, expand ? undefined : MIN_COUNT_ACTIVITIES).map((item) => item.message)}
            </Box>
          );
        })}
      </Box>
      {allActivitiesCount > MIN_COUNT_ACTIVITIES && !expand && (
        <ShowMoreBox
          background={SHOW_MORE_BOX_BACKGROUND}
          height={{ min: '156px' }}
          align="center"
          justify="end"
        >
          <Box margin={{ bottom: '40px' }}>
            <AppButton onClick={() => setExpand(!expand)}>
              {`Show more (${allActivitiesCount - MIN_COUNT_ACTIVITIES})`}
            </AppButton>
          </Box>
        </ShowMoreBox>
      )}
    </Box>
  );
};

export default TransactionActivities;
